import { useCallback, useState } from 'react';
import type { MailClient, MoveStep } from '../api/client';
import { KEYWORD, type Email, unpackId } from '../api/types';
import { useToasts } from '../ui/toasts';
import type { MailboxList } from './useMailboxList';

/*
    Every action lands in the UI immediately and reconciles with the server after.
    A failure rolls the change back rather than leaving the list lying about state —
    silently diverging from the server is worse than a visible error.
*/

export interface MailActions {
    setSeen: (id: string, seen: boolean) => void;
    toggleSeen: (id: string) => void;
    toggleStar: (id: string) => void;
    move: (id: string, mailboxId: string) => void;
    trash: (id: string) => void;
    /** The same actions over a selection. Resolves with what actually happened. */
    bulk: (ids: string[], what: BulkAction, mailboxId?: string) => Promise<{ done: number; failed: number }>;
    error: string | null;
    clearError: () => void;
}

export function useMailActions(
    client: MailClient | undefined,
    accountId: string | undefined,
    list: MailboxList,
    special: { trashId?: string },
    onLeave?: (id: string) => void,
    /** The reader's name for a folder, so an undo offer can say where the message went. */
    nameOf?: (mailboxId: string) => string,
): MailActions {
    const [error, setError] = useState<string | null>(null);
    const { emails, patch, drop, reload } = list;
    const show = useToasts((s) => s.show);

    /*
        Offer the way back.

        Only when the server said where the messages landed: a move it cannot describe is a move
        that cannot be undone, and a button that fails is worse than no button. The undo is an
        ordinary move in the other direction — nothing is held in reserve, nothing is "pending",
        and if the reader ignores the offer nothing further happens.
    */
    const offerUndo = useCallback(
        (steps: MoveStep[], message: string, values?: Record<string, string>) => {
            const undoable = steps.filter((step) => step.ids.length > 0);
            show({
                message,
                values,
                undo: undoable.length
                    ? () => {
                          void Promise.all(
                              undoable.map((step) => client?.move(step.ids, step.from)),
                          )
                              .then(() => reload())
                              .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
                      }
                    : undefined,
            });
        },
        [client, reload, show],
    );

    const setKeyword = useCallback(
        (id: string, keyword: string, value: boolean) => {
            const current = emails[id];
            if (!client || !accountId || !current) return;
            if (!!current.keywords[keyword] === value) return;

            const before = current.keywords;
            const after = { ...before };
            if (value) after[keyword] = true;
            else delete after[keyword];
            patch(id, { keywords: after });

            void client
                .updateEmail(accountId, id, { [`keywords/${keyword}`]: value ? true : null })
                .catch((e: unknown) => {
                    patch(id, { keywords: before });
                    setError(e instanceof Error ? e.message : String(e));
                });
        },
        [client, accountId, emails, patch],
    );

    /*
        Read and unread act on the ROW, and a row in a conversation listing is the whole
        conversation.

        Its weight comes from `threadUnread`, the server's count for the group, not from the flag of
        the one message the row happens to show — so changing only that flag left the row bold
        until the next poll reloaded the list, up to thirty seconds later. And only that one message
        was ever marked: a conversation with two unread messages stayed bold for good, because
        nothing here reached the other one.

        Reading therefore marks every unread message of the conversation. The row's own message
        goes first and without waiting; the rest are found through the thread only when the count
        says there are any, so an ordinary single message still costs one request. Unread marks the
        row's own message, which is what makes a conversation unread again.
    */
    const markSeen = useCallback(
        async (id: string, seen: boolean, known: Email) => {
            if (!client || !accountId) return;
            const flag = { [`keywords/${KEYWORD.seen}`]: seen ? true : null };
            if (!!known.keywords[KEYWORD.seen] !== seen) await client.updateEmail(accountId, id, flag);
            const ownUnread = known.keywords[KEYWORD.seen] ? 0 : 1;
            if (seen && (known.threadUnread ?? 0) > ownUnread) {
                const rest = await client.getThread(id);
                await Promise.all(
                    rest.filter((m) => !m.keywords[KEYWORD.seen]).map((m) => client.updateEmail(accountId, m.id, flag)),
                );
            }
        },
        [client, accountId],
    );

    /*
        Every message a row stands for, in THIS folder.

        Moving or deleting a conversation row used to send the one message the row shows — its
        newest — and leave the rest behind, so the conversation came back as a row on the next load
        (measured locally: two messages in, one deleted, one left, row back). The person's own
        replies filed in Sent belong to the conversation too, but not to this folder: throwing the
        inbox copy away must not throw their Sent mail with it.

        One request for a plain message; the thread is asked for only when the row says it holds
        more than one.
    */
    const rowIds = useCallback(
        async (id: string, known: Email | undefined): Promise<string[]> => {
            if (!client || (known?.threadCount ?? 0) < 2) return [id];
            const here = unpackId(id).folder;
            const rest = await client.getThread(id);
            return [id, ...rest.filter((m) => unpackId(m.id).folder === here).map((m) => m.id)];
        },
        [client],
    );

    // Moves a row to a folder and says where each message went, so the undo can bring all of them back.
    const moveRow = useCallback(
        async (id: string, known: Email | undefined, mailboxId: string) => {
            if (!client) return [];
            const ids = await rowIds(id, known);
            const steps = await client.move(ids, mailboxId);
            for (const one of ids) client.forget(one);
            return steps;
        },
        [client, rowIds],
    );

    const removeRow = useCallback(
        async (id: string, known: Email | undefined) => {
            if (!client) return { erased: false, steps: [] };
            const ids = await rowIds(id, known);
            const { erased, steps } = await client.remove(ids);
            for (const one of ids) client.forget(one);
            return { erased, steps };
        },
        [client, rowIds],
    );

    const setSeen = useCallback(
        (id: string, seen: boolean) => {
            const current = emails[id];
            if (!client || !accountId || !current) return;
            if (rowUnread(current) === !seen && !!current.keywords[KEYWORD.seen] === seen) return;

            const before = { keywords: current.keywords, threadUnread: current.threadUnread };
            patch(id, seenPatch(current, seen));
            void markSeen(id, seen, current).catch((e: unknown) => {
                patch(id, before);
                setError(e instanceof Error ? e.message : String(e));
            });
        },
        [client, accountId, emails, patch, markSeen],
    );

    const toggleSeen = useCallback(
        (id: string) => {
            const current = emails[id];
            if (current) setSeen(id, rowUnread(current));
        },
        [emails, setSeen],
    );

    const toggleStar = useCallback(
        (id: string) => {
            const current = emails[id];
            if (current) setKeyword(id, KEYWORD.flagged, !current.keywords[KEYWORD.flagged]);
        },
        [emails, setKeyword],
    );

    const move = useCallback(
        (id: string, mailboxId: string) => {
            const known = emails[id];
            if (!client || !accountId || !known) return;
            drop(id);
            onLeave?.(id);
            void moveRow(id, known, mailboxId)
                .then((steps) => {
                    const folder = nameOf?.(mailboxId) ?? '';
                    if (mailboxId === special.trashId) offerUndo(steps, 'toast.trashed');
                    else offerUndo(steps, 'toast.movedTo', { folder });
                })
                .catch((e: unknown) => {
                    setError(e instanceof Error ? e.message : String(e));
                    // The optimistic removal was wrong; the server is the truth.
                    reload();
                });
        },
        [client, accountId, emails, drop, reload, onLeave, nameOf, offerUndo, special.trashId, moveRow],
    );

    const trash = useCallback(
        (id: string) => {
            if (!client || !accountId) return;
            if (special.trashId) {
                move(id, special.trashId);
                return;
            }
            // No Trash folder on this account — destroy is the only option, and there is no way
            // back from it. Said plainly rather than offering a button that cannot work.
            const known = emails[id];
            drop(id);
            onLeave?.(id);
            void removeRow(id, known)
                .then(({ erased, steps }) => {
                    if (erased) show({ message: 'toast.erased' });
                    else offerUndo(steps, 'toast.trashed');
                })
                .catch((e: unknown) => {
                    setError(e instanceof Error ? e.message : String(e));
                    reload();
                });
        },
        [client, accountId, emails, special.trashId, move, drop, reload, onLeave, show, offerUndo, removeRow],
    );

    /*
        The same actions over a whole selection.

        One JMAP request per message rather than a single Email/set with many
        updates: the server caps objects per set, a partial failure inside one
        call is awkward to unpick, and this way a message that refuses to move
        doesn't take the other forty with it. Which ones failed is reported.
    */
    const bulk = useCallback(
        async (ids: string[], what: BulkAction, mailboxId?: string) => {
            if (!client || !accountId || !ids.length) return { done: 0, failed: 0 };
            let done = 0;
            let failed = 0;

            for (const id of ids) {
                try {
                    if (what === 'read' || what === 'unread') {
                        const current = emails[id];
                        if (current) {
                            await markSeen(id, what === 'read', current);
                            patch(id, seenPatch(current, what === 'read'));
                        }
                    } else if (what === 'star' || what === 'unstar') {
                        await client.updateEmail(accountId, id, { [`keywords/${KEYWORD.flagged}`]: what === 'star' ? true : null });
                        patch(id, { keywords: withKeyword(emails[id]?.keywords, KEYWORD.flagged, what === 'star') });
                    } else if (what === 'move' && mailboxId) {
                        await moveRow(id, emails[id], mailboxId);
                        drop(id);
                        onLeave?.(id);
                    } else if (what === 'trash') {
                        if (special.trashId) await moveRow(id, emails[id], special.trashId);
                        else await removeRow(id, emails[id]);
                        drop(id);
                        onLeave?.(id);
                    }
                    done++;
                } catch {
                    failed++;
                }
            }

            if (failed) setError(`${failed} of ${ids.length} could not be changed.`);
            return { done, failed };
        },
        [client, accountId, emails, patch, drop, onLeave, special.trashId, markSeen, moveRow, removeRow],
    );

    return {
        setSeen,
        toggleSeen,
        toggleStar,
        move,
        trash,
        bulk,
        error,
        clearError: useCallback(() => setError(null), []),
    };
}

export type BulkAction = 'read' | 'unread' | 'star' | 'unstar' | 'move' | 'trash';

/** Whether the row reads as unread — the conversation's count when it has one, else its own flag. */
export function rowUnread(e: Email): boolean {
    return (e.threadCount ?? 0) > 0 ? (e.threadUnread ?? 0) > 0 : !e.keywords[KEYWORD.seen];
}

/*
    What a row looks like once read or unread, before the server has answered. Read empties the
    conversation's count; unread puts the row's own message back into it.
*/
export function seenPatch(e: Email, seen: boolean): Partial<Email> {
    const keywords = withKeyword(e.keywords, KEYWORD.seen, seen);
    if (!(e.threadCount ?? 0)) return { keywords };
    const count = e.threadUnread ?? 0;
    return { keywords, threadUnread: seen ? 0 : e.keywords[KEYWORD.seen] ? count + 1 : Math.max(count, 1) };
}

function withKeyword(current: Record<string, boolean> | undefined, keyword: string, on: boolean): Record<string, boolean> {
    const next = { ...(current ?? {}) };
    if (on) next[keyword] = true;
    else delete next[keyword];
    return next;
}
